import { useEffect, useState } from "react";
import {
  Building2,
  ClipboardList,
  Pencil,
  FileSpreadsheet,
  Plus,
  Trash2,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/cn";
import { Button } from "@/components/ui/Button";
import { Loader } from "@/components/ui/Loader";
import {
  createFolhaServico,
  deleteFolhaServico,
  listFolhaServicos,
  updateFolhaServico,
  type FolhaServicoDto,
} from "@/services/folhaServicos";
import {
  createFolhaLancamento,
  deleteFolhaLancamento,
  listFolhaLancamentos,
  updateFolhaLancamento,
  type FolhaLancamentoDto,
  type FolhaLancamentoFormValues,
} from "@/services/folhaLancamentos";
import {
  createFolhaColaborador,
  deleteFolhaColaborador,
  listFolhaColaboradores,
  type FolhaColaboradorDto,
} from "@/services/folhaColaboradores";
import { ModalServico } from "./ModalServico";
import { ModalLancamentoFolha } from "./ModalLancamentoFolha";
import { ModalColaborador } from "./ModalColaborador";

type FolhaMenuId = "folhas" | "servicos" | "colaboradores" | "departamentos" | "relatorios";

const MENU_ITEMS: Array<{
  id: FolhaMenuId;
  label: string;
  description: string;
  Icon: LucideIcon;
}> = [
  {
    id: "folhas",
    label: "Folhas de Pagamento",
    description: "Consultar, gerar e gerenciar as folhas do período",
    Icon: FileSpreadsheet,
  },
  {
    id: "servicos",
    label: "Serviços",
    description: "Cadastro de serviços e valores padrão da folha",
    Icon: Wrench,
  },
  {
    id: "colaboradores",
    label: "Colaboradores",
    description: "Cadastro e vínculos dos colaboradores na folha",
    Icon: Users,
  },
  {
    id: "departamentos",
    label: "Departamentos",
    description: "Centros de custo e áreas para rateio da folha",
    Icon: Building2,
  },
  {
    id: "relatorios",
    label: "Relatórios",
    description: "Resumos, comparativos e exportações da folha",
    Icon: ClipboardList,
  },
];

const SECTION_COPY: Record<FolhaMenuId, { title: string; body: string }> = {
  folhas: {
    title: "Folhas de Pagamento",
    body: "Lance os serviços executados por colaborador, casa e quinzena — no mesmo formato da planilha de controle.",
  },
  servicos: {
    title: "Serviços",
    body: "Cadastre os serviços e valores padrão disponíveis para as folhas de pagamento.",
  },
  colaboradores: {
    title: "Colaboradores",
    body: "Vincule colaboradores do Sienge (credores marcados como funcionário) à folha de pagamentos.",
  },
  departamentos: {
    title: "Departamentos",
    body: "Organize departamentos e centros de custo usados na folha.",
  },
  relatorios: {
    title: "Relatórios",
    body: "Consulte relatórios e exportações relacionados à folha de pagamentos.",
  },
};

export function FolhaPagamentosPage() {
  const [active, setActive] = useState<FolhaMenuId>("folhas");
  const [servicos, setServicos] = useState<FolhaServicoDto[]>([]);
  const [lancamentos, setLancamentos] = useState<FolhaLancamentoDto[]>([]);
  const [colaboradores, setColaboradores] = useState<FolhaColaboradorDto[]>([]);

  const [catalogModalOpen, setCatalogModalOpen] = useState(false);
  const [editingServico, setEditingServico] = useState<FolhaServicoDto | null>(null);
  const [lancamentoModalOpen, setLancamentoModalOpen] = useState(false);
  const [editingLancamento, setEditingLancamento] = useState<FolhaLancamentoDto | null>(null);
  const [colaboradorModalOpen, setColaboradorModalOpen] = useState(false);

  const [loadingServicos, setLoadingServicos] = useState(true);
  const [loadingLancamentos, setLoadingLancamentos] = useState(true);
  const [loadingColaboradores, setLoadingColaboradores] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const section = SECTION_COPY[active];

  async function loadServicos() {
    setLoadingServicos(true);
    setError(null);
    try {
      setServicos(await listFolhaServicos());
    } catch {
      setError("Não foi possível carregar os serviços cadastrados.");
    } finally {
      setLoadingServicos(false);
    }
  }

  async function loadLancamentos() {
    setLoadingLancamentos(true);
    setError(null);
    try {
      setLancamentos(await listFolhaLancamentos());
    } catch {
      setError("Não foi possível carregar os lançamentos da folha.");
    } finally {
      setLoadingLancamentos(false);
    }
  }

  async function loadColaboradores() {
    setLoadingColaboradores(true);
    setError(null);
    try {
      setColaboradores(await listFolhaColaboradores());
    } catch {
      setError("Não foi possível carregar os colaboradores cadastrados.");
    } finally {
      setLoadingColaboradores(false);
    }
  }

  useEffect(() => {
    void loadServicos();
    void loadLancamentos();
    void loadColaboradores();
  }, []);

  function openCreateCatalogModal() {
    setEditingServico(null);
    setError(null);
    setCatalogModalOpen(true);
  }

  function openEditCatalogModal(servico: FolhaServicoDto) {
    setEditingServico(servico);
    setError(null);
    setCatalogModalOpen(true);
  }

  function closeCatalogModal() {
    setCatalogModalOpen(false);
    setEditingServico(null);
  }

  async function handleSaveCatalog(data: { nome: string; valorPadrao: number }) {
    if (editingServico) {
      await updateFolhaServico(editingServico.id, {
        nome: data.nome,
        valorUnitario: data.valorPadrao,
      });
    } else {
      await createFolhaServico({
        nome: data.nome,
        valorUnitario: data.valorPadrao,
      });
    }
    await loadServicos();
  }

  async function handleDeleteCatalog(servico: FolhaServicoDto) {
    if (!window.confirm(`Excluir o serviço "${servico.nome}"?`)) return;
    setError(null);
    try {
      await deleteFolhaServico(servico.id);
      if (editingServico?.id === servico.id) closeCatalogModal();
      await loadServicos();
    } catch {
      setError("Não foi possível excluir o serviço.");
    }
  }

  function openCreateLancamentoModal() {
    setEditingLancamento(null);
    setError(null);
    setLancamentoModalOpen(true);
  }

  function openEditLancamentoModal(lancamento: FolhaLancamentoDto) {
    setEditingLancamento(lancamento);
    setError(null);
    setLancamentoModalOpen(true);
  }

  function closeLancamentoModal() {
    setLancamentoModalOpen(false);
    setEditingLancamento(null);
  }

  async function handleSaveLancamento(data: FolhaLancamentoFormValues) {
    if (editingLancamento) {
      await updateFolhaLancamento(editingLancamento.id, data);
    } else {
      await createFolhaLancamento(data);
    }
    await loadLancamentos();
  }

  async function handleDeleteLancamento(lancamento: FolhaLancamentoDto) {
    if (!window.confirm(`Excluir o lançamento de "${lancamento.servicoNome}"?`)) return;
    setError(null);
    try {
      await deleteFolhaLancamento(lancamento.id);
      if (editingLancamento?.id === lancamento.id) closeLancamentoModal();
      await loadLancamentos();
    } catch {
      setError("Não foi possível excluir o lançamento.");
    }
  }

  function openCreateColaboradorModal() {
    setError(null);
    setColaboradorModalOpen(true);
  }

  async function handleSaveColaborador(siengeId: number) {
    await createFolhaColaborador(siengeId);
    await loadColaboradores();
  }

  async function handleDeleteColaborador(colaborador: FolhaColaboradorDto) {
    if (!window.confirm(`Remover o colaborador "${colaborador.nome}" da folha?`)) return;
    setError(null);
    try {
      await deleteFolhaColaborador(colaborador.id);
      await loadColaboradores();
    } catch {
      setError("Não foi possível remover o colaborador.");
    }
  }

  const currency = new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  return (
    <div className="h-full min-h-0 flex flex-col gap-4 p-4 sm:p-6">
      <header className="flex-shrink-0">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Folha de Pagamentos</h1>
        <p className="text-sm text-slate-500 mt-1">
          Selecione uma área do módulo para continuar
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 flex-shrink-0">
        {MENU_ITEMS.map(({ id, label, description, Icon }) => {
          const selected = active === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setActive(id)}
              className={cn(
                "text-left rounded-2xl border p-4 transition-all duration-200",
                "hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
                selected
                  ? "border-emerald-500 bg-emerald-50 shadow-sm"
                  : "border-slate-200 bg-white",
              )}
            >
              <div
                className={cn(
                  "w-11 h-11 rounded-xl flex items-center justify-center mb-3",
                  selected ? "bg-emerald-600 text-white" : "bg-emerald-100 text-emerald-700",
                )}
              >
                <Icon className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <p className="font-semibold text-slate-900 text-sm sm:text-base leading-tight">
                {label}
              </p>
              <p className="text-xs sm:text-sm text-slate-500 mt-1 leading-snug">
                {description}
              </p>
            </button>
          );
        })}
      </div>

      <section className="flex-1 min-h-0 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-2xl">{section.body}</p>
          </div>
          {active === "folhas" && (
            <Button
              type="button"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={openCreateLancamentoModal}
            >
              Cadastrar serviço
            </Button>
          )}
          {active === "colaboradores" && (
            <Button
              type="button"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={openCreateColaboradorModal}
            >
              Cadastrar colaborador
            </Button>
          )}
        </div>

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}

        {active === "folhas" ? (
          <div className="mt-6 min-w-0">
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-emerald-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-700">
                  <tr>
                    <th className="px-3 py-3">Quinzena</th>
                    <th className="px-3 py-3">Função</th>
                    <th className="px-3 py-3">Colaborador</th>
                    <th className="px-3 py-3">Casa</th>
                    <th className="px-3 py-3">Serviço</th>
                    <th className="px-3 py-3 text-right">Valor pagamento</th>
                    <th className="px-3 py-3 text-right">Saldo</th>
                    <th className="px-3 py-3 text-right">Valor do serviço</th>
                    <th className="px-3 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loadingLancamentos ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8">
                        <div className="flex justify-center">
                          <Loader size="md" label="Carregando lançamentos…" />
                        </div>
                      </td>
                    </tr>
                  ) : lancamentos.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                        Nenhum serviço lançado na folha. Use o botão Cadastrar serviço.
                      </td>
                    </tr>
                  ) : (
                    lancamentos.map((item) => (
                      <tr key={item.id} className="text-slate-700">
                        <td className="px-3 py-3 whitespace-nowrap">{item.quinzena}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{item.funcao}</td>
                        <td className="px-3 py-3 font-medium text-slate-900">{item.colaborador}</td>
                        <td className="px-3 py-3 whitespace-nowrap">{item.casa}</td>
                        <td className="px-3 py-3 min-w-[220px]">{item.servicoNome}</td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          {currency.format(item.valorPagamento)}
                        </td>
                        <td
                          className={cn(
                            "px-3 py-3 text-right whitespace-nowrap",
                            item.saldo > 0 && "bg-emerald-50 font-medium text-emerald-800",
                          )}
                        >
                          {currency.format(item.saldo)}
                        </td>
                        <td className="px-3 py-3 text-right whitespace-nowrap">
                          {currency.format(item.valorDoServico)}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              aria-label={`Editar lançamento de ${item.servicoNome}`}
                              onClick={() => openEditLancamentoModal(item)}
                              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Excluir lançamento de ${item.servicoNome}`}
                              onClick={() => void handleDeleteLancamento(item)}
                              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ModalLancamentoFolha
              open={lancamentoModalOpen}
              onClose={closeLancamentoModal}
              onSave={handleSaveLancamento}
              servicosCatalogo={servicos}
              lancamento={editingLancamento}
            />
          </div>
        ) : active === "servicos" ? (
          <div className="mt-6 min-w-0">
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={openCreateCatalogModal}
              >
                Cadastrar serviço
              </Button>
            </div>

            <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">Nome do serviço</th>
                    <th className="px-4 py-3 text-right">Valor padrão</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loadingServicos ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8">
                        <div className="flex justify-center">
                          <Loader size="md" label="Carregando serviços…" />
                        </div>
                      </td>
                    </tr>
                  ) : servicos.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                        Nenhum serviço cadastrado.
                      </td>
                    </tr>
                  ) : (
                    servicos.map((servico) => (
                      <tr key={servico.id} className="text-slate-700">
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">{servico.id}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{servico.nome}</td>
                        <td className="px-4 py-3 text-right">
                          {currency.format(servico.valorUnitario)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              aria-label={`Editar ${servico.nome}`}
                              onClick={() => openEditCatalogModal(servico)}
                              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              aria-label={`Excluir ${servico.nome}`}
                              onClick={() => void handleDeleteCatalog(servico)}
                              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ModalServico
              open={catalogModalOpen}
              onClose={closeCatalogModal}
              onSave={handleSaveCatalog}
              servico={editingServico}
            />
          </div>
        ) : active === "colaboradores" ? (
          <div className="mt-6 min-w-0">
            <div className="overflow-x-auto rounded-xl border border-slate-200">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-4 py-3">ID</th>
                    <th className="px-4 py-3">ID Sienge</th>
                    <th className="px-4 py-3">Nome</th>
                    <th className="px-4 py-3">Documento</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {loadingColaboradores ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8">
                        <div className="flex justify-center">
                          <Loader size="md" label="Carregando colaboradores…" />
                        </div>
                      </td>
                    </tr>
                  ) : colaboradores.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                        Nenhum colaborador cadastrado. Use o botão Cadastrar colaborador para
                        buscar no Sienge.
                      </td>
                    </tr>
                  ) : (
                    colaboradores.map((colaborador) => (
                      <tr key={colaborador.id} className="text-slate-700">
                        <td className="px-4 py-3">{colaborador.id}</td>
                        <td className="px-4 py-3">{colaborador.siengeId}</td>
                        <td className="px-4 py-3 font-medium text-slate-900">{colaborador.nome}</td>
                        <td className="px-4 py-3">{colaborador.documento || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              aria-label={`Remover ${colaborador.nome}`}
                              onClick={() => void handleDeleteColaborador(colaborador)}
                              className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <ModalColaborador
              open={colaboradorModalOpen}
              onClose={() => setColaboradorModalOpen(false)}
              onSave={handleSaveColaborador}
            />
          </div>
        ) : (
          <div className="mt-8 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-400">
            Conteúdo desta área em construção
          </div>
        )}
      </section>
    </div>
  );
}
